import { ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import {
    type Key,
    ToggleButton,
    ToggleButtonGroup,
} from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { cn } from "tailwind-variants";
import {
    usePostUmbracoApiV1UmbrellaPageSurveys,
    usePutUmbracoApiV1UmbrellaPageSurveysId,
} from "#/_generated/umbraco/page-survey-api/features/page-surveys/page-surveys.ts";
import type {
    UpdatePageSurveyRatedQuestionRequest,
    UpdatePageSurveyRequest,
} from "#/_generated/umbraco/page-survey-api/models";
import Spinner from "#/_packages/umbrella/components/ui/spinners";
import { Alert } from "#/_packages/umbrella/features/alerts/components/alert.tsx";
import { Rating } from "#/_packages/umbrella/features/rating/components/rating.tsx";
import { m } from "#/paraglide/messages";
import { getLocale } from "#/paraglide/runtime";

const tagButtonCss =
    "inline-flex items-center gap-2 rounded-full px-2.5 py-1.5 data-[selected=true]:border data-[selected=true]:border-2 data-[selected=true]:border-primary";

export type PageSurveyQuestion = {
    id: string;
    label: string;
};

export type PageSurveySubmitPayload = {
    helpful: boolean;
    helpfulLabel?: string;
    ratings: Array<{ question: string; rating: number }>;
    comment?: string;
};

export type PageSurveyProps = {
    /** The Umbraco content key for the page receiving the response. */
    umbracoContentId: string;
    /** Overrides the question stored as the API survey topic. */
    voteQuestion?: string;
    votePositiveLabel?: string;
    voteNegativeLabel?: string;
    heading?: string;
    introHtml?: string;
    questions?: PageSurveyQuestion[];
    enableComment?: boolean;
    commentLabel?: string;
    commentHelpTextHtml?: string;
    submitBtnLabel?: string;
    errorMessage?: string;
    confirmationMessageHtml?: string;
    className?: string;
};

export function PageSurvey(props: PageSurveyProps) {
    return <PageSurveySession key={props.umbracoContentId} {...props} />;
}

function PageSurveySession({
    umbracoContentId,
    voteQuestion,
    votePositiveLabel,
    voteNegativeLabel,
    heading,
    introHtml,
    questions = [],
    enableComment = false,
    commentLabel,
    commentHelpTextHtml,
    submitBtnLabel,
    errorMessage,
    confirmationMessageHtml,
    className,
}: PageSurveyProps) {
    const [pageSurveyId, setPageSurveyId] = useState<number | null>(null);
    const [selectedAnswer, setSelectedAnswer] = useState<Set<Key>>(new Set());
    const [isFormOpen, setIsFormOpen] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!isFormOpen) containerRef.current?.querySelector("button")?.focus();
    }, [isFormOpen]);
    const {
        mutateAsync: createSurvey,
        isPending,
        isError,
    } = usePostUmbracoApiV1UmbrellaPageSurveys();
    const resolvedVoteQuestion = voteQuestion ?? m.page_survey_vote_question();
    const resolvedConfirmationMessage =
        confirmationMessageHtml ?? m.page_survey_confirmation();
    const resolvedErrorMessage =
        errorMessage ?? "We could not submit your response. Please try again.";
    const shouldShowExtendedForm = questions.length > 0 || enableComment;

    const handleVote = async (answer: string) => {
        setSelectedAnswer(new Set([answer]));
        setIsFormOpen(true);
        // Resume the same response and draft rather than recording another vote.
        if (pageSurveyId !== null) return;

        try {
            const response = await createSurvey({
                data: {
                    culture: getLocale(),
                    topic: voteQuestion ?? resolvedVoteQuestion,
                    answer,
                    umbracoContentId,
                },
            });
            setPageSurveyId(response.data.id);
        } catch {
            // The mutation state renders the configured error message below.
        }
    };

    return (
        <div ref={containerRef} className={cn("container", className)}>
            <section className="block__page-survey">
                <div className="read-width">
                    {pageSurveyId !== null && isFormOpen ? null : (
                        <SurveyVoteQuestion
                            voteQuestion={resolvedVoteQuestion}
                            voteNegativeLabel={voteNegativeLabel}
                            votePositiveLabel={votePositiveLabel}
                            selectedAnswer={isFormOpen ? selectedAnswer : new Set()}
                            isDisabled={isPending}
                            onVote={handleVote}
                        />
                    )}

                    {isPending ? <Spinner intent="primary" /> : null}
                    {isError ? (
                        <div className={"mt-5"}>
                            <Alert intent={"danger"}>{resolvedErrorMessage}</Alert>
                        </div>
                    ) : null}

                    {pageSurveyId !== null ? (
                        shouldShowExtendedForm ? (
                            <div hidden={!isFormOpen}>
                                <ExtendedPageSurvey
                                    heading={heading}
                                    commentLabel={commentLabel}
                                    introHtml={introHtml}
                                    commentHelpTextHtml={commentHelpTextHtml}
                                    questions={questions}
                                    pageSurveyId={pageSurveyId}
                                    topic={voteQuestion ?? resolvedVoteQuestion}
                                    answer={[...selectedAnswer][0]?.toString() ?? ""}
                                    umbracoContentId={umbracoContentId}
                                    submitBtnLabel={submitBtnLabel}
                                    enableComment={enableComment}
                                    errorMessage={resolvedErrorMessage}
                                    confirmationMessageHtml={resolvedConfirmationMessage}
                                    onClose={() => setIsFormOpen(false)}
                                />
                            </div>
                        ) : (
                            <ThankYouMessage
                                confirmationMessageHtml={resolvedConfirmationMessage}
                            />
                        )
                    ) : null}
                </div>
            </section>
        </div>
    );
}

type ExtendedPageSurveyFormValues = {
    comment: string;
    ratings: Record<string, number>;
};

function ExtendedPageSurvey({
    heading,
    introHtml,
    commentLabel,
    commentHelpTextHtml,
    questions,
    pageSurveyId,
    topic,
    answer,
    umbracoContentId,
    submitBtnLabel,
    enableComment,
    errorMessage,
    confirmationMessageHtml,
    onClose,
}: {
    heading?: string;
    introHtml?: string;
    commentLabel?: string;
    commentHelpTextHtml?: string;
    questions: PageSurveyQuestion[];
    pageSurveyId: number;
    topic: string;
    answer: string;
    umbracoContentId: string;
    submitBtnLabel?: string;
    enableComment: boolean;
    errorMessage: string;
    confirmationMessageHtml: string;
    onClose: () => void;
}) {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const commentId = useId();
    const {
        mutateAsync: updateSurvey,
        isPending,
        isError,
    } = usePutUmbracoApiV1UmbrellaPageSurveysId();
    const { control, handleSubmit, register } =
        useForm<ExtendedPageSurveyFormValues>({
            defaultValues: {
                comment: "",
                ratings: Object.fromEntries(
                    questions.map((question) => [question.id, 0]),
                ),
            },
        });

    const onSubmit = async (values: ExtendedPageSurveyFormValues) => {
        const data: SubmitPageSurveyRequest = {
            topic,
            answer,
            umbracoContentId,
            comment: values.comment.trim() || undefined,
            ratedQuestions: questions.map((question) => ({
                question: question.label,
                rating: values.ratings[question.id] ?? 0,
            })),
        };

        try {
            await updateSurvey({ id: pageSurveyId, data });
            setIsSubmitted(true);
        } catch {
            // The mutation state renders the configured error message below.
        }
    };

    if (isSubmitted)
        return (
            <ThankYouMessage confirmationMessageHtml={confirmationMessageHtml} />
        );

    return (
        <div className="mt-4">
            <div className="card card-body border-border-base bg-surface-base relative rounded-xl border p-6 md:p-8">
                <button
                    type="button"
                    aria-label={m.common_close()}
                    onClick={onClose}
                    className="text-text-base hover:bg-surface-muted focus-visible:ring-focus-ring absolute top-2 right-2 inline-flex size-10 items-center justify-center rounded focus-visible:ring-2 focus-visible:outline-none"
                >
                    <X size={20} aria-hidden="true" />
                </button>
                {heading ? (
                    <p className="text-text-base mr-5 text-xl font-semibold">{heading}</p>
                ) : null}
                {introHtml ? (
                    <div
                        className="rich-text mb-4"
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is authored in the trusted CMS rich-text field.
                        dangerouslySetInnerHTML={{ __html: introHtml }}
                    />
                ) : null}

                <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                    {questions.length > 0 ? (
                        <div className="border-border-base flex w-full flex-col border-b">
                            {questions.map((question) => (
                                <Controller
                                    key={question.id}
                                    control={control}
                                    name={`ratings.${question.id}`}
                                    render={({ field }) => (
                                        <Rating
                                            label={question.label}
                                            value={field.value ?? 0}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            ))}
                        </div>
                    ) : null}

                    {enableComment ? (
                        <div className="mt-5">
                            {commentLabel ? (
                                <label
                                    className="form-label text-text-base mb-2 block font-medium"
                                    htmlFor={commentId}
                                >
                                    {commentLabel}
                                </label>
                            ) : null}
                            <textarea
                                id={commentId}
                                className="form-control border-border-base bg-surface-base text-text-base focus-visible:border-brand-primary focus-visible:ring-brand-primary/20 w-full rounded-lg border px-3 py-2 transition outline-none focus-visible:ring-2"
                                {...register("comment")}
                                onInput={(event) => {
                                    const textarea = event.currentTarget;
                                    textarea.style.height = "auto";
                                    textarea.style.height = `${textarea.scrollHeight}px`;
                                }}
                            />
                            {commentHelpTextHtml ? (
                                <div
                                    className="rich-text form-text mb-0-last text-text-muted mt-1 text-sm"
                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is authored in the trusted CMS rich-text field.
                                    dangerouslySetInnerHTML={{ __html: commentHelpTextHtml }}
                                />
                            ) : null}
                        </div>
                    ) : null}

                    <div className="mt-5">
                        <button
                            type="submit"
                            disabled={isPending}
                            className="btn btn-primary bg-brand-primary text-brand-primary-contrast hover:bg-brand-primary-dim focus-visible:ring-focus-ring inline-flex self-start rounded px-4 py-2 text-sm font-semibold transition focus-visible:ring-2 focus-visible:outline-none"
                        >
                            {submitBtnLabel ?? m.form_submit_button()}
                        </button>
                    </div>

                    {isPending ? <Spinner intent="primary" /> : null}
                    {isError ? (
                        <div className="mt-5">
                            <Alert intent="danger">{errorMessage}</Alert>
                        </div>
                    ) : null}
                </form>
            </div>
        </div>
    );
}

type SubmitPageSurveyRequest = UpdatePageSurveyRequest & {
    comment?: string;
    ratedQuestions: UpdatePageSurveyRatedQuestionRequest[];
};

function ThankYouMessage({
    confirmationMessageHtml,
}: {
    confirmationMessageHtml: string;
}) {
    return (
        <div
            className="rich-text survey-confirmation card card-body mb-0-last border-border-base bg-surface-base relative mt-5 rounded-xl border p-6 md:p-8"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is authored in the trusted CMS rich-text field.
            dangerouslySetInnerHTML={{ __html: confirmationMessageHtml }}
        />
    );
}

function SurveyVoteQuestion({
    voteQuestion,
    votePositiveLabel,
    voteNegativeLabel,
    selectedAnswer,
    isDisabled,
    onVote,
}: {
    voteQuestion: string;
    votePositiveLabel?: string;
    voteNegativeLabel?: string;
    selectedAnswer: Set<Key>;
    isDisabled: boolean;
    onVote: (answer: string) => Promise<void>;
}) {
    const resolvedVotePositiveLabel = votePositiveLabel ?? m.page_survey_yes();
    const resolvedVoteNegativeLabel = voteNegativeLabel ?? m.page_survey_no();

    return (
        <div className="flex items-center gap-3">
            <span>{voteQuestion}</span>
            <ToggleButtonGroup
                selectedKeys={selectedAnswer}
                selectionMode="single"
                disallowEmptySelection
                isDisabled={isDisabled}
                onSelectionChange={(answers) => {
                    const answer = [...answers][0];
                    if (answer !== undefined) void onVote(answer.toString());
                }}
            >
                <ToggleButton
                    id={resolvedVotePositiveLabel}
                    aria-label={resolvedVotePositiveLabel}
                    className={tagButtonCss}
                >
                    <ThumbsUp size={22} />
                    <span className="underline underline-offset-2">
                        {resolvedVotePositiveLabel}
                    </span>
                </ToggleButton>
                <ToggleButton
                    id={resolvedVoteNegativeLabel}
                    aria-label={resolvedVoteNegativeLabel}
                    className={tagButtonCss}
                >
                    <ThumbsDown size={22} />
                    <span className="underline underline-offset-2">
                        {resolvedVoteNegativeLabel}
                    </span>
                </ToggleButton>
            </ToggleButtonGroup>
        </div>
    );
}
